import React from 'react';

import Message from './Message';
import Image from './Image';
import Title from './Title';
import Buttons from './Buttons';
import Video from './Video';
import Thumbnail from './Thumbnail';
import UserReaction from './UserReaction';
import Embed from './Embed';
import BackButton from './BackButton';

const JPEG_TYPE = 'image/jpeg';
const PNG_TYPE = 'image/png';
const GIF_TYPE = 'image/gif';
const HERO_TYPE = 'application/vnd.microsoft.card.hero';
const VIDEO_TYPE = 'application/vnd.microsoft.card.video';
const THUMBNAIL_TYPE = 'application/vnd.microsoft.card.thumbnail';
const PDF_TYPE = 'application/pdf';
const EMBED_TYPE = 'embed';

const sectionComponents = {
  message: Message,
  image: Image,
  title: Title,
  buttons: Buttons,
  video: Video,
  thumbnail: Thumbnail,
  pdf: Message,
  userReaction: UserReaction,
  embed: Embed,
  backButton: BackButton
};

// The reason for building up the array of section components instead of using JSX to express
// the components to include is that we need to control the border property for each child
// component depending on whether it is the first or last for a message.
// (Putting the radius on a wrapper component would not work).

// This approach works quite elegantly, if we did want to use JSX we could have a
// WithSectionPosition component that does a React.children.map to extend the
// the component's props with isStart and isEnd

// https://reactjs.org/docs/react-api.html#reactchildrenmap
const generateSections = (activity, attachmentIndex = 0) => {
  const sections = [];

  const isIE11 = !!(window.MSInputMethodContext && document.documentMode);

  if (activity.text) {
    sections.push({ type: 'message', text: activity.text });
  }

  // Thumbs up & down section(Multiple attachments only render once)
  if (
    attachmentIndex === 0 &&
    activity.entities &&
    activity.entities[0] &&
    activity.entities[0].marker &&
    activity.entities[0].marker.showReactions
  ) {
    sections.push({ type: 'userReaction' });
  }

  // Back button section
  if (
    attachmentIndex === 0 &&
    activity.entities &&
    activity.entities[0] &&
    activity.entities[0].marker &&
    activity.entities[0].marker.showBackButton &&
    activity.isLatestMessage
  ) {
    sections.push({ type: 'backButton' });
  }

  // Check that we've got a message with at least one attachment
  if (!(activity.attachments && activity.attachments[0])) {
    return sections;
  }

  const attachment = activity.attachments[attachmentIndex];
  const contentType = attachment.contentType;
  const card = attachment.content;

  if (activity.attachmentLayout === EMBED_TYPE) {
    sections.push({ type: EMBED_TYPE, src: attachment.url });
  }

  if (contentType === THUMBNAIL_TYPE) {
    const imageSrc = card.images && card.images[0] && card.images[0].url;
    sections.push({
      type: 'thumbnail',
      title: card.title,
      subtitle: card.subtitle,
      imageSrc,
      zeroFlexShrink: isIE11,
      flexGrow: !card.text
    });

    if (card.text) {
      // per https://ambitai.atlassian.net/browse/CHAT-116
      // we don't want the message background color and width from message section
      // so we must use the title for the thumbnail card
      sections.push({ type: 'title', text: card.text, flexGrow: true });
    }

    if (card.buttons && !activity.suggestedActions) {
      sections.push({ type: 'buttons', buttons: card.buttons });
    }

    return sections;
  }

  if (contentType === JPEG_TYPE || contentType === PNG_TYPE || contentType === GIF_TYPE) {
    sections.push({ type: 'image', src: attachment.contentUrl });
  }

  if (contentType === PDF_TYPE) {
    sections.push({ type: 'pdf', src: attachment.contentUrl, text: attachment.name });
  }

  // Check that we're dealing with a card
  if (
    !(contentType === HERO_TYPE || contentType === VIDEO_TYPE || contentType === THUMBNAIL_TYPE)
  ) {
    return sections;
  }

  if (card.images && card.images[0] && card.images[0].url) {
    sections.push({ type: 'image', src: attachment.content.images[0].url, zeroFlexShrink: isIE11 });
  }

  if (contentType === VIDEO_TYPE && card.media && card.media[0]) {
    sections.push({ type: 'video', src: attachment.content.media[0].url });
  }

  if (card.title || card.subtitle || card.text) {
    sections.push({
      type: 'title',
      title: card.title,
      subtitle: card.subtitle,
      subtitleStyles: card.subtitleStyles,
      text: card.text,
      flexGrow: true
    });
  }

  if (card.buttons && !activity.suggestedActions) {
    sections.push({ type: 'buttons', buttons: card.buttons });
  }

  return sections;
};

const renderSection = (section, activity, isStart, isEnd) => {
  const Component = sectionComponents[section.type];
  // Check if the section is an action type (thumbs up/down or back button)
  const isActionSection = section.type === 'userReaction' || section.type === 'backButton';

  // Keep the original isEnd state for main content
  // Action buttons should not affect the border radius
  const shouldHaveEndRadius = isEnd && !isActionSection;

  return (
    <Component
      key={section.type}
      {...section}
      isStart={isStart}
      isEnd={shouldHaveEndRadius}
      id={activity.id}
      activity={activity}
      userAction={activity.userAction}
      marker={(activity.entities && activity.entities[0] && activity.entities[0].marker) || null}
    />
  );
};

const renderAttachment = (activity, attachmentIndex = 0) => {
  const sections = generateSections(activity, attachmentIndex);

  // Find the last non-action section index
  const lastContentIndex = sections.reduce((lastIndex, section, index) => {
    if (section.type !== 'userReaction' && section.type !== 'backButton') {
      return index;
    }
    return lastIndex;
  }, 0);

  return {
    sections: sections.map((section, i) =>
      renderSection(section, activity, i === 0, i === lastContentIndex)
    ),
    fullWidth: !!sections.find(section => section.type === 'image')
  };
};

export default renderAttachment;



// WEBPACK FOOTER //
// ./src/components/MessageHistory/BotMessage/renderAttachment.js